import React, { useRef, useState } from 'react'
import { useMutation, useQuery, gql } from '@apollo/client'
import { validateFunc } from '../../constraints/constraints'
import { withTranslation } from 'react-i18next'
import { Box, Typography, TextField, Button, Alert, Paper, InputAdornment, CircularProgress } from '@mui/material'
import { editTipping, getTipping, createTipping } from '../../apollo'
import SaveIcon from '@mui/icons-material/Save'
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'

const GET_TIPPING = gql`${getTipping}`
const EDIT_TIPPING = gql`${editTipping}`
const CREATE_TIPPING = gql`${createTipping}`

function Tipping(props) {
  const { t } = props
  const formRef = useRef()
  const [tip1Error, setTip1Error] = useState(null)
  const [tip2Error, setTip2Error] = useState(null)
  const [tip3Error, setTip3Error] = useState(null)
  const [mainError, mainErrorSetter] = useState('')
  const [success, successSetter] = useState('')

  const onCompleted = () => {
    successSetter(t('TippingUpdated'))
    setTip1Error(null); setTip2Error(null); setTip3Error(null)
    mainErrorSetter('')
    setTimeout(() => { mainErrorSetter(''); successSetter('') }, 3000)
  }
  const onError = error => {
    let message = ''
    try { message = error.graphQLErrors[0].message } catch { message = t('ActionFailedTryAgain') }
    successSetter('')
    mainErrorSetter(message)
    setTimeout(() => { mainErrorSetter(''); successSetter('') }, 3000)
  }

  const onSubmitValidation = () => {
    const form = formRef.current
    const tip1Errors = !validateFunc({ tip: form.tip1.value }, 'tip')
    const tip2Errors = !validateFunc({ tip: form.tip2.value }, 'tip')
    const tip3Errors = !validateFunc({ tip: form.tip3.value }, 'tip')
    setTip1Error(tip1Errors); setTip2Error(tip2Errors); setTip3Error(tip3Errors)
    if (!(tip1Errors && tip2Errors && tip3Errors)) mainErrorSetter(t('FieldsRequired'))
    return tip1Errors && tip2Errors && tip3Errors
  }

  const { data } = useQuery(GET_TIPPING, { onError })
  // data.tips is an array — use [0]
  const tipping = data?.tips?.[0]
  const mutation = tipping?._id ? EDIT_TIPPING : CREATE_TIPPING
  const [mutate, { loading }] = useMutation(mutation, { onError, onCompleted })

  const tipFields = [
    { name: 'tip1', label: 'Tip Option 1', num: '1', error: tip1Error, default: tipping?.tipVariations?.[0] ?? '' },
    { name: 'tip2', label: 'Tip Option 2', num: '2', error: tip2Error, default: tipping?.tipVariations?.[1] ?? '' },
    { name: 'tip3', label: 'Tip Option 3', num: '3', error: tip3Error, default: tipping?.tipVariations?.[2] ?? '' },
  ]

  return (
    <Paper elevation={0} sx={{ borderRadius: '20px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 4px 24px rgba(0,0,0,0.06)', maxWidth: 560 }}>
      <Box sx={{ px: 3, py: 2.5, borderBottom: '1px solid #f1f5f9', background: '#fafbfc', display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ width: 38, height: 38, borderRadius: '10px', bgcolor: '#eff6ff', border: '1px solid #bfdbfe', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <AttachMoneyIcon sx={{ fontSize: 20, color: '#3b82f6' }} />
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#1e293b' }}>Tip Variations</Typography>
          <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', mt: 0.2 }}>Set three tip amount options for customers</Typography>
        </Box>
      </Box>

      <Box sx={{ p: 3 }}>
        <form ref={formRef}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {tipFields.map(field => (
              <Box key={field.name}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                  <Box sx={{ width: 20, height: 20, borderRadius: '6px', bgcolor: '#eff6ff', border: '1px solid #bfdbfe', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography sx={{ fontSize: '0.65rem', fontWeight: 800, color: '#3b82f6' }}>{field.num}</Typography>
                  </Box>
                  <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#374151' }}>{field.label}</Typography>
                </Box>
                <TextField
                  key={field.name + field.default}
                  name={field.name}
                  type="number"
                  fullWidth
                  size="small"
                  inputProps={{ min: 0, step: 1 }}
                  defaultValue={field.default}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><AttachMoneyIcon sx={{ fontSize: 16, color: '#94a3b8' }} /></InputAdornment>,
                    sx: { borderRadius: '10px', fontSize: '0.9rem', bgcolor: '#fff' }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: field.error === false ? '#fca5a5' : field.error === true ? '#86efac' : '#e2e8f0' },
                      '&:hover fieldset': { borderColor: '#93c5fd' },
                      '&.Mui-focused fieldset': { borderColor: '#3b82f6' }
                    }
                  }}
                />
                {field.error === false && <Typography sx={{ fontSize: '0.72rem', color: '#ef4444', mt: 0.5, ml: 0.5 }}>Valid tip amount required</Typography>}
              </Box>
            ))}

            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={15} color="inherit" /> : <SaveIcon />}
              disabled={loading}
              onClick={e => {
                e.preventDefault()
                if (onSubmitValidation()) {
                  const form = formRef.current
                  mutate({
                    variables: {
                      tippingInput: {
                        _id: tipping?._id || '',
                        tipVariations: [Number(form.tip1.value), Number(form.tip2.value), Number(form.tip3.value)],
                        enabled: true
                      }
                    }
                  })
                }
              }}
              sx={{ background: 'linear-gradient(135deg,#3b82f6,#2563eb)', color: '#fff', fontWeight: 700, px: 3, py: 1.1, borderRadius: '10px', boxShadow: '0 4px 14px rgba(59,130,246,0.4)', textTransform: 'none', alignSelf: 'flex-start', '&:hover': { background: 'linear-gradient(135deg,#2563eb,#1d4ed8)', transform: 'translateY(-1px)' }, transition: 'all 0.2s ease' }}>
              {loading ? 'Saving...' : 'Save Tips'}
            </Button>
          </Box>
        </form>

        <Box mt={2}>
          {success && <Alert severity="success" variant="filled" sx={{ borderRadius: '10px', fontSize: '0.83rem' }}>{success}</Alert>}
          {mainError && <Alert severity="error" variant="filled" sx={{ borderRadius: '10px', fontSize: '0.83rem' }}>{mainError}</Alert>}
        </Box>
      </Box>
    </Paper>
  )
}

export default withTranslation()(Tipping)